import { useQuery } from '@tanstack/react-query'
import { CalendarClock, MonitorPlay, Pencil, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toApiError } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { runQuery, useDeleteRun } from '@/features/courses/api'
import { RunDialog } from '@/features/courses/components/RunDialog'
import { SessionList } from '@/features/courses/components/SessionList'
import { STATUS_LABEL, STATUS_TONE } from '@/features/courses/status'
import { formatRange, scheduleSummary } from '@/features/courses/format'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** One scheduled course: group, teacher, dates, weekly sessions and every session. */
export function CourseRunPage() {
  const { t } = useTranslation('courses')
  const id = Number(useParams().id)
  const navigate = useNavigate()
  const run = useQuery(runQuery(id))
  const remove = useDeleteRun()
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  useDocumentTitle(run.data?.course.name ?? 'Scheduled courses')
  if (run.isPending) return <LoadingState />
  if (run.error) return <ErrorState onRetry={() => void run.refetch()} />
  const r = run.data

  return (
    <>
      <PageHeader
        title={`${r.course.name} · ${r.section.label}`}
        description={formatRange(r.startsOn, r.endsOn)}
        breadcrumbs={[
          { label: t('Courses') },
          { label: t('Scheduled courses'), to: '/admin/courses/runs' },
          { label: r.course.name },
        ]}
        homeHref="/admin"
        actions={
          <>
            <Button variant="secondary" leftIcon={<MonitorPlay />} asChild>
              <Link to={`/admin/lms/courses/${r.section.id}/${r.course.subject.id}`}>
                {t('Online course')}
              </Link>
            </Button>
            <Button variant="secondary" leftIcon={<Pencil />} onClick={() => setEditing(true)}>
              {t('Edit')}
            </Button>
            <IconButton
              label={t('Delete')}
              icon={<Trash2 />}
              variant="danger-soft"
              onClick={() => setDeleting(true)}
            />
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Card className="self-start">
          <CardHeader
            title={t('Overview')}
            actions={<Badge tone={STATUS_TONE[r.status]}>{t(STATUS_LABEL[r.status])}</Badge>}
          />
          <CardBody className="space-y-3 text-sm">
            <p>
              <span className="text-muted">{t('Course')}: </span>
              <Link
                to={`/admin/courses/${r.course.id}`}
                className="font-medium text-brand-700 hover:underline"
              >
                {r.course.name}
              </Link>
            </p>
            <p>
              <span className="text-muted">{t('Group')}: </span>
              {r.section.label}
            </p>
            <p>
              <span className="text-muted">{t('Teacher')}: </span>
              {r.teacher?.name ?? t('Not assigned')}
            </p>
            <p>
              <span className="text-muted">{t('Weekly sessions')}: </span>
              {scheduleSummary(r.slots)}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <Users className="size-4 text-muted" aria-hidden="true" />
              {t('{{count}} students', { count: r.learnerCount })}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <CalendarClock className="size-4 text-muted" aria-hidden="true" />
              {t('{{done}} of {{total}} sessions', { done: r.sessionsDone, total: r.sessionCount })}
            </p>
            {r.notes ? <p className="whitespace-pre-wrap text-ink-soft">{r.notes}</p> : null}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t('Sessions')} description={t('School holidays are skipped.')} />
          <CardBody>
            <SessionList sessions={r.sessions ?? []} />
          </CardBody>
        </Card>
      </div>
      <RunDialog open={editing} onOpenChange={setEditing} run={r} />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('Delete this scheduled course?')}
        description={t('The content already copied into online learning stays.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(r.id, {
            onSuccess: () => void navigate('/admin/courses/runs'),
            onError: (e) => toast.error(t('Could not delete'), toApiError(e).message),
          })
        }
      />
    </>
  )
}
